import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "react-simple-snackbar";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Typography,
  Box,
  Chip,
  InputAdornment,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import PeopleIcon from "@material-ui/icons/People";
import HistoryIcon from "@material-ui/icons/History";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import { getEmployees, createEmployee, updateEmployeeAction } from "../../actions/employeeActions";
import Spinner from "../Spinner/Spinner";
import NoData from "../svgIcons/NoData";

const SIDEBAR_WIDTH = 80;

const useStyles = makeStyles((theme) => ({
  root: {
    marginLeft: SIDEBAR_WIDTH,
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    minHeight: "100vh",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    fontWeight: 600,
    fontSize: "1.5rem",
  },
  titleIcon: {
    fontSize: 28,
    color: theme.palette.primary.main,
  },
  addButton: {
    textTransform: "none",
    fontWeight: 600,
    padding: theme.spacing(1.25, 2),
    boxShadow: theme.shadows[2],
    "&:hover": {
      boxShadow: theme.shadows[4],
    },
  },
  filterBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.paper,
      borderRadius: 8,
    },
  },
  tableCard: {
    borderRadius: 12,
    boxShadow: theme.shadows[2],
    overflow: "auto",
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  table: {
    minWidth: 560,
  },
  statusColumn: {
    minWidth: 160,
    width: 160,
    boxSizing: "border-box",
  },
  statusCellContent: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1.5),
    minWidth: 140,
  },
  tableHeadCell: {
    padding: theme.spacing(2),
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.grey[100],
  },
  tableCell: {
    padding: theme.spacing(2),
    fontSize: "0.9375rem",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  statusChip: {
    fontWeight: 500,
    minWidth: 72,
  },
  statusActive: {
    backgroundColor: theme.palette.success.light || "#c8e6c9",
    color: theme.palette.success.dark || "#2e7d32",
  },
  statusInactive: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[700],
  },
  editButton: {
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  dialogContent: {
    paddingTop: theme.spacing(1),
    minWidth: 320,
  },
  modifiedDate: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 8,
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
  },
  emptyState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    padding: theme.spacing(8, 3),
    margin: theme.spacing(4, 0),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 12,
    border: `1px dashed ${theme.palette.grey[400]}`,
  },
  emptyStateIcon: {
    marginBottom: theme.spacing(2),
    opacity: 0.85,
  },
  emptyStateTitle: {
    fontWeight: 600,
    fontSize: "1.125rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  emptyText: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    maxWidth: 320,
    fontSize: "0.9375rem",
  },
}));

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const EmployeeMaster = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const [openSnackbar] = useSnackbar();
  const user = JSON.parse(localStorage.getItem("profile"));

  const { employees, isLoading } = useSelector((state) => state.employees || { employees: [], isLoading: false });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({ name: "", active: true });
  const [searchQuery, setSearchQuery] = useState("");

  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) return employees;
    const q = searchQuery.trim().toLowerCase();
    return employees.filter((e) => (e.name || "").toLowerCase().includes(q));
  }, [employees, searchQuery]);

  // Run fetch exactly once on mount. Empty deps = run once only (avoids loop from history/redux updates).
  useEffect(() => {
    const profile = JSON.parse(localStorage.getItem("profile"));
    if (!profile) {
      history.push("/login");
      return;
    }
    dispatch(getEmployees());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenAdd = () => {
    setEditingEmployee(null);
    setFormData({ name: "", active: true });
    setDialogOpen(true);
  };

  const handleOpenEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name || "",
      active: employee.active !== false,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEmployee(null);
    setFormData({ name: "", active: true });
  };

  const handleSubmit = async () => {
    if (!formData.name?.trim()) {
      openSnackbar("Name is required");
      return;
    }
    if (editingEmployee) {
      await dispatch(
        updateEmployeeAction(editingEmployee._id, formData, openSnackbar)
      );
    } else {
      await dispatch(createEmployee(formData, openSnackbar));
    }
    handleCloseDialog();
  };

  const handleToggleActive = (row) => async () => {
    const newActive = !row.active;
    await dispatch(
      updateEmployeeAction(
        row._id,
        { name: row.name, active: newActive },
        openSnackbar
      )
    );
  };

  if (!user) return null;

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          paddingTop: "20px",
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h5" className={classes.title}>
          <PeopleIcon className={classes.titleIcon} />
          Employee Master
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<HistoryIcon />}
            onClick={() => history.push("/shift-logs")}
            className={classes.addButton}
          >
            View Shift Logs
          </Button>
          <Button
            className={classes.addButton}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
          >
            Add Employee
          </Button>
        </Box>
      </Box>

      {employees.length > 0 && (
        <Box className={classes.filterBox}>
          <TextField
            placeholder="Search by name..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            style={{ minWidth: 240 }}
          />
          {searchQuery && (
            <Typography variant="body2" color="textSecondary">
              {filteredEmployees.length} of {employees.length} employee(s)
            </Typography>
          )}
        </Box>
      )}

      {employees.length === 0 ? (
        <Paper elevation={0} className={classes.emptyState}>
          <Box className={classes.emptyStateIcon}>
            <NoData />
          </Box>
          <Typography className={classes.emptyStateTitle}>No Data</Typography>
          <Typography className={classes.emptyText}>
            No employees to display. Click &quot;Add Employee&quot; to add your first employee.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} className={classes.tableCard}>
          <Table className={classes.table} aria-label="Employee master table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>Name</TableCell>
                <TableCell className={`${classes.tableHeadCell} ${classes.statusColumn}`} align="center">
                  Status
                </TableCell>
                <TableCell className={classes.tableHeadCell}>Created At</TableCell>
                <TableCell className={classes.tableHeadCell}>Modified At</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmployees.map((row) => (
                <TableRow key={row._id} className={classes.tableRow} hover>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body1" style={{ fontWeight: 500 }}>
                      {row.name}
                    </Typography>
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.statusColumn}`} align="center">
                    <Box className={classes.statusCellContent}>
                      <Chip
                        size="small"
                        label={row.active ? "Active" : "Inactive"}
                        className={`${classes.statusChip} ${
                          row.active ? classes.statusActive : classes.statusInactive
                        }`}
                      />
                      <Switch
                        checked={row.active}
                        onChange={handleToggleActive(row)}
                        color="primary"
                      />
                    </Box>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatDate(row.createdAt)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatDate(row.updatedAt)}
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(row)}
                      aria-label="edit"
                      className={classes.editButton}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 12, zIndex: 10000 } }}
      >
        <DialogTitle style={{ paddingBottom: 8 }}>
          {editingEmployee ? "Edit Employee" : "Add Employee"}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) =>
              setFormData({ ...formData, name: e.target.value })
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) =>
                  setFormData({ ...formData, active: e.target.checked })
                }
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {formData.active ? "Active" : "Inactive"}
              </Typography>
            }
            style={{ marginTop: 16 }}
          />
          {editingEmployee && editingEmployee.updatedAt && (
            <Box className={classes.modifiedDate}>
              <Typography variant="caption" display="block" color="textSecondary">
                Last modified: {formatDate(editingEmployee.updatedAt)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            {editingEmployee ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeMaster;
